/**
 * The glassware's visual language — a soft flat-vector look (pale blue-grey
 * glass, tonal outlines, a left-lit sheen, a lighter band at the liquid's
 * surface) rather than the thin-black-outline textbook diagram the bench used
 * to be drawn in. Kept out of the scene component so the palette is one thing
 * to change, not fifteen literals scattered through the JSX.
 */

/* The bench surface used to be three constants here, deep navy, on the reasoning
 * that the lab lives inside a dark app and a white panel punched into it would
 * read as a hole. That stopped being true when the bench went onto a light site,
 * so the surface is now `--lab-bench-*` in ChemistryBench.css and follows the
 * page. Glassware below is unchanged: pale glass and tonal outlines read on
 * either ground. */

/** Glass body: a gentle top-to-bottom gradient, never a flat fill. */
export const GLASS_TOP = '#e9f3fa';
export const GLASS_BOTTOM = '#b9cfe2';
/** How see-through the glass body itself is (the gradient's stopOpacity) —
 *  separate from the liquid inside, which stays fully opaque. Low enough
 *  that an empty vessel reads as glass, not a solid pale-blue shape. */
export const GLASS_OPACITY = 0.2;
/** The thicker collar of glass at a vessel's mouth. */
export const GLASS_LIP = '#aec6da';
/** Outlines are tonal blue-grey — black reads as a diagram, not as glass. */
export const GLASS_STROKE = '#93b2c9';
export const GLASS_STROKE_WIDTH = 2.6;
/** The rim band's own edges, drawn lighter than the silhouette. They are
 *  interior detail rather than the outline of the object, and the band they
 *  bound is only about a pixel tall at the front and back — a flat ring
 *  foreshortens to almost nothing there, correctly — so a full-weight stroke
 *  on BOTH edges swallows the band whole and the mouth reads as a heavy hoop
 *  laid on the glass instead of the thickness of the glass itself. */
export const GLASS_RIM_STROKE_WIDTH = 1.5;
/** Faint tick marks etched on the glass (a beaker's graduations). */
export const GLASS_ETCH = '#7e96ab';

/** Painted lab steel — stands, tripods, clamps. Charcoal, not chrome: the
 *  reference apparatus is dark against pale glassware, which is what keeps
 *  the two families of object readable side by side. */
export const STEEL = '#39445a';
export const STEEL_LIGHT = '#69769a';
export const BRASS = '#d3a534';

/** Rack timber. */
export const WOOD = '#b8834a';
export const WOOD_DARK = '#8d5f2e';
export const WOOD_LIGHT = '#d6a76a';

/** Flame: a saturated orange body with a hot, pale core. */
export const FLAME_OUTER = '#f7962c';
export const FLAME_INNER = '#ffd977';

/** Mixes `hex` towards white by `amount` (0-1). Used for the lighter band at
 *  the liquid's surface and the meniscus, both of which the reference art
 *  renders as the same hue simply lifted, not as a separate colour. */
export function lighten(hex: string, amount: number): string {
  const raw = hex.replace('#', '');
  const full = raw.length === 3 ? raw.split('').map(c => c + c).join('') : raw;
  const num = parseInt(full, 16);
  const mix = (channel: number): number => Math.round(channel + (255 - channel) * amount);
  const r = mix((num >> 16) & 255), g = mix((num >> 8) & 255), b = mix(num & 255);
  return `#${[r, g, b].map(v => v.toString(16).padStart(2, '0')).join('')}`;
}

/** Darkens `hex` towards black by `amount` (0-1) — the liquid's shaded floor. */
export function darken(hex: string, amount: number): string {
  const raw = hex.replace('#', '');
  const full = raw.length === 3 ? raw.split('').map(c => c + c).join('') : raw;
  const num = parseInt(full, 16);
  const mix = (channel: number): number => Math.round(channel * (1 - amount));
  const r = mix((num >> 16) & 255), g = mix((num >> 8) & 255), b = mix(num & 255);
  return `#${[r, g, b].map(v => v.toString(16).padStart(2, '0')).join('')}`;
}

/** Linearly interpolates from `a` toward `b` by `t` (0 = a, 1 = b) — used to
 *  fade the liquid's own colour from its pre-reaction state toward its
 *  post-precipitation residual state as a settling solid carries the
 *  colour away with it, rather than snapping between the two. */
export function mixColors(a: string, b: string, t: number): string {
  const parse = (hex: string): [number, number, number] => {
    const raw = hex.replace('#', '');
    const full = raw.length === 3 ? raw.split('').map(c => c + c).join('') : raw;
    const num = parseInt(full, 16);
    return [(num >> 16) & 255, (num >> 8) & 255, num & 255];
  };
  const [ar, ag, ab] = parse(a);
  const [br, bg, bb] = parse(b);
  const mix = (x: number, y: number): number => Math.round(x + (y - x) * t);
  const r = mix(ar, br), g = mix(ag, bg), bch = mix(ab, bb);
  return `#${[r, g, bch].map(v => v.toString(16).padStart(2, '0')).join('')}`;
}
